import { Button, Divider, FormControl, Grid, TextField, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
import {
  employeeResignation,
  uploadEmployeeDocsById
} from 'src/services/employeeService';
import { AppActions } from 'src/store/AppReducer';
import { DatePicker } from '@mui/lab';

const ResignationForm = ({ toggle, employeeId }) => {
  const { register, handleSubmit, reset } = useForm<any>();
  const navigate = useNavigate();
  const [attachment, setAttachment] = useState<any>({});
  const dispatch = useDispatch();

  const onSubmit = () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const body = {
        employeeId: employeeId,
        resignDate: startDate,
        lastWorkingDate: endDate,
        resignationFeedback: feedback,
        resigningEmployeeId : user.EmployeeId
      };
      dispatch(AppActions.showLoader());
      employeeResignation(body)
        .then((res) => {
          if (attachment) {
            uploadEmployeeDocsById(employeeId, attachment.file);
          }
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: 'You’ve confirmed the resignation successfully.',
              severity: 'success'
            })
          );
          navigate('/employees');
        })
        .catch((e) => {
          dispatch(AppActions.hideLoader());
        });
  };

  const TextFieldStyle = {
    paddingTop: 10,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingTop: 10,
    paddingLeft: 10,
    paddingRight: 10,
    fontSize: '13px'
  };

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [feedback, setFeedback] = useState('');

  const changeHandler = (event) => {
    const fileSize = (event.target.files[0].size / 1024 / 1024).toFixed(4);
    const imageName = event.target.files[0].name;
    const extensionsRegex =
      /(\.pdf|\.xls|\.xlsx|\.doc|\.docx|\.png|\.jpeg|\.jpg)$/i;

    if (Number(fileSize) > 10) {
      setErrors((prev) => ({
        ...prev,
        attachmentError: 'Maximum file size exceeded.'
      }));
    } else if (!extensionsRegex.exec(imageName)) {
      setErrors((prev) => ({
        ...prev,
        attachmentError: 'File format is incorrect. Please check and try again.'
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        attachmentError: ''
      }));
      setAttachment({ file: event.target.files[0] });
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState({
    nameError: '',
    tierError: '',
    feedbackError: '',
    attachmentError: '',
    resignationDateError: '',
    lastWorkingDateError: ''
  });

  const toggleConfirm = () => {
    const errorData: any = {};

    if (!startDate) {
      errorData.resignationDateError = 'Resignation Date is Required';
    }
    if (!endDate) {
      errorData.lastWorkingDateError = 'Last Working Date is Required';
    }
    if (feedback === '') {
      errorData.feedbackError = 'Feedback Required';
    }

    setErrors(errorData);
    if (Object.keys(errorData).length === 0) {
      setIsOpen(!isOpen);
    }
  };

  const { isLoading } = useAppSelector((state) => state.appReducer);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid sx={{ px: 2, mt: '0.5rem' }} container spacing={2}>
        <Grid item xs={6}>
          <Typography
            sx={{ pb: 1 }}
            variant="h4"
            style={{
              paddingTop: 10,
              paddingLeft: 10,
              paddingRight: 10,
              fontSize: '15px'
            }}
          >
            Resignation
          </Typography>
        </Grid>
      </Grid>
      <Grid sx={{ px: 2 }} container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography style={LabelStyle} variant="h5">
            Date of Resignation<span style={{ color: 'red' }}>*</span>
          </Typography>
          <div style={{ marginLeft: 10, marginRight: 8, marginTop: 10}}>
            <FormControl fullWidth>
              <DatePicker
                InputProps={{
                  className: 'date-picker'
                }}
                renderInput={(params) => (
                  <TextField {...params} id="resignation-startDate" />
                )}
                value={startDate ? startDate : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setStartDate(moment(newValue).format('YYYY-MM-DD'));
                    setErrors({
                      ...errors,
                      resignationDateError: ''
                    });
                  }
                }}
              />
            </FormControl>
          </div>
          {errors?.resignationDateError && (
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {errors?.resignationDateError}&nbsp;
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography style={LabelStyle} variant="h5">
            Last Working Date<span style={{ color: 'red' }}>*</span>
          </Typography>
          <div style={{ marginLeft: 10, marginRight: 10, marginTop: 10}}>
            <FormControl fullWidth>
              <DatePicker
                disabled={!startDate}
                InputProps={{
                  className: 'date-picker'
                }}
                minDate={new Date(startDate)}
                renderInput={(params) => (
                  <TextField {...params} id="resignation-startDate" />
                )}
                value={endDate ? endDate : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setEndDate(moment(newValue).format('YYYY-MM-DD'));
                    setErrors({
                      ...errors,
                      lastWorkingDateError: ''
                    });
                  }
                }}
              />
            </FormControl>
          </div>
          {errors?.lastWorkingDateError && (
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {errors?.lastWorkingDateError}&nbsp;
            </Typography>
          )}
        </Grid>
        <Grid item style={{ marginTop: '-1rem' }} xs={12}>
          <Typography style={LabelStyle} variant="h5">
            Feedback
          </Typography>
          <TextField
            id="resignation-feedback"
            multiline={true}
            rows={2}
            {...register('feedback', {})}
            style={TextFieldStyle}
            size="medium"
            fullWidth
            placeholder="Enter Feedback"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value.length > 500) {
                setErrors((prev) => ({
                  ...prev,
                  feedbackError: 'Character length should not exceed 500'
                }));
              } else {
                setErrors((prev) => ({
                  ...prev,
                  feedbackError: ''
                }));
              }
              setFeedback(e.target.value);
            }}
          />
          {errors?.feedbackError && (
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {errors?.feedbackError}&nbsp;
            </Typography>
          )}
        </Grid>
        <>
          <Grid item style={{ marginTop: '-1rem' }} xs={5}>
            <Typography style={LabelStyle} fontWeight="h5" variant="h5">
              Add Attachment
            </Typography>
            <TextField
              id="resignation-attachment"
              style={TextFieldStyle}
              fullWidth
              size="small"
              placeholder="No file Selected"
              value={
                Object.keys(attachment).length === 0
                  ? ''
                  : attachment?.file?.name
              }
            />
            <p
              style={{
                marginLeft: '.75rem',
                marginTop: 0,
                marginBottom: 0,
                fontSize: '0.6rem'
              }}
            >
              Maximum file upload size: 10MB
            </p>
            {errors?.attachmentError && (
              <Typography
                style={{
                  paddingLeft: 15,
                  paddingRight: 15,
                  fontSize: '11px'
                }}
                color="red"
              >
                {errors?.attachmentError}
              </Typography>
            )}
          </Grid>
          <Grid item xs={2}>
            <Button
              id="btn-resignation-changeHandler"
              style={{
                marginTop: '1.3rem',
                border: '1px solid #5569ff',
                marginLeft: '-0.5rem',
                color: '#5569ff',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 'normal'
              }}
            >
              Choose
              <input
                type="file"
                name="attachment"
                id="upload-attachment"
                accept=".pdf, .xls, .xlsx, .doc, .docx, .png, .jpeg, .jpg"
                onChange={changeHandler}
              />
            </Button>
          </Grid>
        </>
        <Grid sx={{ pt: 2 }} container spacing={2}>
          <Divider />
          <Grid item xs={6}></Grid>

          <Grid
            item
            sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}
            xs={6}
          >
            <Button
              id="btn-resignation-toggle"
              size="small"
              sx={{
                width: '30%',
                background: '#F5F7FF',
                fontSize: '13px',
                fontWeight: 'normal'
              }}
              onClick={() => toggle()}
            >
              Cancel
            </Button>
            <Button
              id="btn-resignation-toggleConfirm"
              size="small"
              sx={{
                width: '30%',
                ml: 3,
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4'
              }}
              variant="contained"
              type="button"
              onClick={() => toggleConfirm()}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmationModal
        isLoading={isLoading}
        open={isOpen}
        toggle={() => setIsOpen(!isOpen)}
        message={`Are you sure you want to confirm this employee’s resignation?`}
        onConfirm={() => onSubmit()}
        noToggle={() => {
          setIsOpen(!isOpen);
        }}
      />
    </form>
  );
};

export default ResignationForm;
